/* eslint-disable react/display-name */

import _ from 'lodash';
import color from 'color';
import React, { FC, ElementType } from 'react';
import MuiButton, { ButtonClassKey, ButtonProps as MuiButtonProps } from '@material-ui/core/Button';

import { Intent } from '../theme';
import { makeStyles, mergeClasses } from '../styles';

export { MuiButton };

const lighten = (themeColor: string, a = 0.1) => color(themeColor).alpha(a).toString();

const darken = (themeColor: string) => color(themeColor).darken(0.1).toString();

const useStyles = makeStyles<IBaseButtonProps>(theme => ({
  label: {
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: 0,
  },
  contained: props =>
    props.type === 'primary'
      ? {
          background: theme.palette.primary.main,
          color: theme.colors.white,
          '&:hover, &:focus': {
            background: darken(theme.palette.primary.main),
          },
        }
      : props.type === 'secondary'
      ? {
          background: lighten(theme.colors.blue, 0.2),
          color: theme.colors.blue,
          '&:hover, &:focus': {
            background: lighten(theme.colors.blue, 0.3),
          },
        }
      : props.type === 'tertiary'
      ? props.mode === 'dark'
        ? {
            color: theme.colors.white,
            background: theme.colors.coolGray70,
            '&:hover, &:focus': {
              background: lighten('white', 0.2),
            },
          }
        : {
            background: theme.colors.gray10,
            '&:hover, &:focus': {
              background: lighten('black', 0.2),
            },
          }
      : props.type === 'success'
      ? {
          color: theme.colors.white,
          background: theme.palette.success.main,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.success.main)} !important`,
          },
        }
      : props.type === 'info'
      ? {
          color: theme.colors.white,
          background: theme.palette.info.main,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.info.main)} !important`,
          },
        }
      : props.type === 'warning'
      ? {
          color: theme.colors.black,
          background: theme.palette.warning.main,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.warning.main)} !important`,
          },
        }
      : props.type === 'error'
      ? {
          color: theme.colors.white,
          background: theme.palette.error.main,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.error.main)} !important`,
          },
        }
      : props.disabled
      ? {
          background: theme.colors.gray10,
          color: theme.colors.gray20,
        }
      : {},
  outlined: props =>
    props.type === 'primary'
      ? {
          background: theme.colors.white,
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          '&:hover, &:focus': {
            background: darken(theme.palette.primary.main),
          },
        }
      : props.type === 'secondary'
      ? {
          background: theme.colors.white,
          color: lighten(theme.colors.blue, 0.7),
          border: `1px solid ${lighten(theme.colors.blue, 0.7)}`,
          '&:hover, &:focus': {
            background: lighten(theme.colors.blue, 0.3),
          },
        }
      : props.type === 'tertiary'
      ? {
          background: theme.colors.white,
          color: theme.colors.gray10,
          border: `1px solid ${theme.colors.gray10}`,
          '&:hover, &:focus': {
            background: lighten('black', 0.2),
          },
        }
      : props.type === 'success'
      ? {
          color: theme.palette.success.main,
          background: theme.colors.white,
          border: `1px solid ${theme.palette.success.main}`,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.success.main)} !important`,
          },
        }
      : props.type === 'info'
      ? {
          color: theme.palette.info.main,
          background: theme.colors.white,
          border: `1px solid ${theme.palette.info.main}`,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.info.main)} !important`,
          },
        }
      : props.type === 'warning'
      ? {
          color: theme.palette.warning.main,
          background: theme.colors.white,
          border: `1px solid ${theme.palette.warning.main}`,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.warning.main)} !important`,
          },
        }
      : props.type === 'error'
      ? {
          color: theme.palette.error.main,
          background: theme.colors.white,
          border: `1px solid ${theme.palette.error.main}`,
          '&:hover, &:focus': {
            background: `${darken(theme.palette.error.main)} !important`,
          },
        }
      : props.disabled
      ? {
          background: theme.colors.gray10,
          color: theme.colors.gray20,
        }
      : {},
  text: props => ({
    padding: '0 8px !important',
    ...(props.type === 'primary'
      ? {
          color: theme.colors.blue,
          '&:hover, &:focus': {
            background: lighten(theme.colors.blue),
          },
        }
      : props.type === 'tertiary'
      ? {
          '&:hover, &:focus': {
            background: lighten('black'),
          },
        }
      : props.type === 'success'
      ? {
          color: theme.palette.success.main,
          '&:hover, &:focus': {
            background: `${lighten(theme.palette.success.light)} !important`,
          },
        }
      : props.type === 'info'
      ? {
          color: theme.palette.info.main,
          '&:hover, &:focus': {
            background: `${lighten(theme.palette.info.light)} !important`,
          },
        }
      : props.type === 'warning'
      ? {
          color: theme.palette.warning.main,
          '&:hover, &:focus': {
            background: `${lighten(theme.palette.warning.light)} !important`,
          },
        }
      : props.type === 'error'
      ? {
          color: theme.palette.error.main,
          '&:hover, &:focus': {
            background: `${lighten(theme.palette.error.light)} !important`,
          },
        }
      : props.disabled
      ? { color: lighten('black', 0.2) }
      : {}),
  }),
  startIcon: {
    marginLeft: 0,
  },
  endIcon: {
    marginRight: 0,
  },
}));

interface IBaseButtonProps {
  variant?: 'contained' | 'text' | 'outlined';
  className?: string;
  children?: React.ReactNode;
  type: 'primary' | 'secondary' | 'tertiary' | Intent;
  mode?: 'dark' | 'light';
  htmlType?: 'submit' | 'button';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  onClick?: (event: any) => void;
  href?: string;
  target?: string;
  rel?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  style?: React.CSSProperties;
  classes?: {
    root?: string;
    label?: string;
    contained?: string;
  };
  component?: ElementType;
  color?: string;
  id?: string;
}

const allowedProps: Array<keyof IBaseButtonProps> = [
  'id',
  'children',
  'className',
  'startIcon',
  'endIcon',
  'onClick',
  'href',
  'disabled',
  'fullWidth',
  'variant',
  'style',
  'component',
  'target',
  'rel',
  'color',
];

export const BaseButton = React.forwardRef<HTMLButtonElement, IBaseButtonProps>((props, ref) => {
  const muiProps = _.pickBy(
    props,
    (prop, key: keyof IBaseButtonProps) => allowedProps.includes(key) || key.startsWith('data-'),
  );

  const classes = useStyles(_.omit(props, 'classes'));

  return (
    <MuiButton
      ref={ref}
      type={props.htmlType}
      classes={mergeClasses<ButtonClassKey>(classes, props.classes)}
      {...(muiProps as MuiButtonProps)}
    />
  );
});

export type ButtonProps = Omit<IBaseButtonProps, 'type'> & {
  type?: IBaseButtonProps['type'];
  children?: React.ReactNode;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return (
    <BaseButton
      ref={ref}
      {...props}
      type={props.type || 'tertiary'}
      variant={props.variant || 'contained'}
      mode={props.mode || 'light'}
    />
  );
});

type TextButtonProps = Omit<IBaseButtonProps, 'type'> & {
  type?: Exclude<IBaseButtonProps['type'], 'secondary'>;
};

export const TextButton: FC<TextButtonProps> = props => {
  return <BaseButton {...props} type={props.type || 'tertiary'} variant="text" />;
};
